import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';

import { groupBy, checkVaccine, formatPatientId } from '../../utils/utilities';
// import ViewVaccine from './ViewVaccine';
import apiClient from '../../utils/config/apiClient';
import { toast } from 'sonner';
import { Popover } from 'antd';

const ImmunizationChart = ({  patient }) => {
	const [loading, setLoading] = useState(false);
	const [records, setRecords] = useState([]);
    const token = localStorage.getItem('token');

	const dates = [
		'Birth',
		'6 wks',
		'10 wks',
		'14 wks',
		'16 wks',
		'6 months',
		'7 months',
		'9 months',
		'12 months',
		'13 months',
		'18 months',
		'2 years',
		'5 years',
		patient?.gender?.toLowerCase() === 'female' && '9 years',
		patient?.gender?.toLowerCase() === 'female' && '9 years + 1',
		patient?.gender?.toLowerCase() === 'female' && '9 years + 6',
		'10 years',
		patient?.gender?.toLowerCase() === 'female' && '10 years',
	].filter(Boolean);

	const periods = records.reduce((acc, items) => {
		items.forEach(item => {
			if (item.period) {
				acc.add(item.period);
			}
		});
		return acc;
	}, new Set());
	const visibleDates = dates.filter(date => periods.has(date));

	useEffect(() => {
		if (!patient) {
			return;
		}

		const fetchImmunization = async () => {
			try {
				setLoading(true);
				const url = `public/portal?url=patient/immunization/${patient.id}`;
				const rs = await apiClient.get(url,{
                headers: {
                    Authorization: "Bearer " + token,
                    },
            });

				const list = groupBy(rs?.data, 'slug');
				setRecords(Object.values(list));
			} catch (error) {
				toast.error(error.message || 'Could not load chart');
			} finally {
				setLoading(false);
			}
		};

		fetchImmunization();
	}, [patient, token]);

	const formatDate = dateString => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return new Intl.DateTimeFormat('en-GB', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
		}).format(date);
	};

	const getStatusMeta = data => {
		const statusClass = checkVaccine(data);
		const dateSource =
			statusClass === 'green'
				? data.date_administered
				: data.scheduled_date || data.next_due_date || data.date_due;
		const dateText = formatDate(dateSource);

		if (statusClass === 'green') {
			return {
				statusClass,
				statusText: 'Administered',
				statusSymbol: '✓',
				statusIcon: 'fa fa-check-circle',
				dateText,
				bgColor: '#d4edda',
			};
		}

		if (statusClass === 'yellow') {
			return {
				statusClass,
				statusText: 'Due',
				statusSymbol: '!',
				statusIcon: 'fa fa-exclamation-circle',
				dateText,
				bgColor: '#fff3cd',
			};
		}

		if (statusClass === 'red') {
			return {
				statusClass,
				statusText: 'Overdue',
				statusSymbol: '!',
				statusIcon: 'fa fa-exclamation-triangle',
				dateText,
				bgColor: '#f8d7da',
			};
		}

		return {
			statusClass,
			statusText: 'Upcoming',
			statusSymbol: '○',
			statusIcon: 'fa fa-clock-o',
			dateText,
			bgColor: '#e9ecef',
		};
	};

	const renderPopoverContent = data => {
		const statusMeta = getStatusMeta(data);

		return (
			<div className="vaccine-popover-card">
				<div className={`vaccine-popover-badge ${statusMeta.statusClass}`}>{statusMeta.statusText}</div>
				<div className="vaccine-popover-row">
					<span>Vaccine</span>
					<strong>{data.name_of_vaccine}</strong>
				</div>
				<div className="vaccine-popover-row">
					<span>Period</span>
					<strong>{data.period || '--'}</strong>
				</div>
				<div className="vaccine-popover-row">
					<span>Recorded date</span>
					<strong>{statusMeta.dateText || '--'}</strong>
				</div>
				{data.date_administered && (
					<div className="vaccine-popover-row">
						<span>Administered</span>
						<strong>{formatDate(data.date_administered)}</strong>
					</div>
				)}
				{!data.date_administered && data.date_due && (
					<div className="vaccine-popover-row">
						<span>Due date</span>
						<strong>{formatDate(data.date_due)}</strong>
					</div>
				)}
				{!data.date_administered && data.scheduled_date && (
					<div className="vaccine-popover-row">
						<span>Scheduled</span>
						<strong>{formatDate(data.scheduled_date)}</strong>
					</div>
				)}
				{!data.date_administered && data.next_due_date && (
					<div className="vaccine-popover-row">
						<span>Next due</span>
						<strong>{formatDate(data.next_due_date)}</strong>
					</div>
				)}
				{data.description && <div className="vaccine-popover-note">{data.description}</div>}
			</div>
		);
	};

	const exportToPDF = () => {
		// Create a hidden iframe for printing
		const printFrame = document.createElement('iframe');
		printFrame.style.position = 'absolute';
		printFrame.style.width = '0';
		printFrame.style.height = '0';
		printFrame.style.border = 'none';
		document.body.appendChild(printFrame);

		const printDocument = printFrame.contentWindow.document;

		// Get the current styles
		const styles = document.querySelectorAll('style, link[rel="stylesheet"]');
		let stylesHTML = '';
		styles.forEach(style => {
			if (style.tagName === 'STYLE') {
				stylesHTML += style.outerHTML;
			} else if (style.tagName === 'LINK') {
				stylesHTML += style.outerHTML;
			}
		});

		// Calculate font sizes based on number of columns and rows
		const columnCount = visibleDates.length + 1;
		const rowCount = records.length;
		let fontSize = '9px';
		let headerFontSize = '9px';
		let titleFontSize = '10px';
		let cellPadding = '3px';
		let headerPadding = '2px';

		// Aggressive scaling for many columns
		if (columnCount > 15) {
			fontSize = '6px';
			headerFontSize = '6.5px';
			titleFontSize = '7px';
			cellPadding = '1.5px';
			headerPadding = '1px';
		} else if (columnCount > 12) {
			fontSize = '6.5px';
			headerFontSize = '7px';
			titleFontSize = '8px';
			cellPadding = '2px';
			headerPadding = '1.5px';
		} else if (columnCount > 10) {
			fontSize = '7px';
			headerFontSize = '8px';
			titleFontSize = '8px';
			cellPadding = '2px';
			headerPadding = '2px';
		}

		// Adjust for many rows
		if (rowCount > 20) {
			fontSize = `${parseInt(fontSize) - 0.5}px`;
			cellPadding = '1.5px';
		}

		// Build the table HTML with hospital details and logo
		const tableHTML = `
		<div class="immunization-print-container" style="padding: 2px; height: 100%; background: white;">
			<!-- Hospital Header with Logo -->
			<div class="hospital-header" style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; border-bottom: 1px solid #ccc; padding-bottom: 3px;">
				<div style="display: flex; align-items: center; gap: 8px;">
					<img src="/deda_image_icon.png" alt="Deda Hospital Logo" style="height: 35px; width: auto;" onerror="this.style.display='none'" />
					<div>
						<h2 style="margin: 0; font-size: 14px; color: #2c3e50;">DEDA HOSPITAL</h2>
						<div style="font-size: 6px; color: #666;">Plot 1847, B07 Cadastral Zone, Behind ABC Cargo, Katampe, Abuja, Nigeria</div>
					</div>
				</div>
				<div style="text-align: right; font-size: 6px;">
					<div>📞 07000013332 | 08184227707</div>
					<div>✉️ info@dedahospital.com</div>
				</div>
			</div>
			
			<!-- Immunization Chart Title -->
			<div class="print-header" style="text-align: center; margin-bottom: 3px;">
				<h3 style="margin: 0 0 2px 0; font-size: ${parseInt(titleFontSize) + 1}px;">Immunization Chart</h3>
				<h4 style="margin: 0 0 2px 0; font-size: ${parseInt(titleFontSize)}px;">${patient?.other_names} ${patient?.surname}</h4>
				<div style="font-size: 6px; margin-bottom: 2px;">
					DOB: ${patient?.date_of_birth ? formatDate(patient.date_of_birth) : 'N/A'} | 
					Hospital No: ${formatPatientId(patient) || 'N/A'} | 
					Generated: ${new Date().toLocaleDateString()}
				</div>
			</div>
			
			<!-- Immunization Table -->
			<div class="table-responsive" style="overflow: visible;">
				<table class="immunization-table" style="width: 100%; border-collapse: collapse; font-size: ${fontSize};">
					<thead>
						<tr>
							<th style="border: 0.5px solid #000; padding: ${headerPadding}; background-color: #f0f0f0; text-align: left; font-weight: bold; width: 150px;">
								Vaccine
							</th>
								${visibleDates
									.map(title => {
										return `
											<th style="border: 0.5px solid #000; padding: ${headerPadding}; background-color: #f0f0f0; text-align: center;">
												<div><strong style="font-size: ${headerFontSize};">${title}</strong></div>
										</th>
									`;
								})
								.join('')}
						</tr>
					</thead>
					<tbody>
						${records
							.map(items => {
								const item = items[0];
								return `
									<tr>
										<td style="border: 0.5px solid #000; padding: ${cellPadding}; background-color: #fff;">
											<strong style="font-size: ${headerFontSize};">${item.name_of_vaccine}</strong>
											<div style="font-size: 5.5px; color: #666; margin-top: 1px;">${item.description || ''}</div>
										</td>
										${visibleDates
											.map(date => {
												const data = items.find(i => i.period === date);
												if (data) {
													const statusMeta = getStatusMeta(data);
													const statusClass = statusMeta.statusClass;
													let bgColor = '';
													let statusSymbol = '';
													let dateText = '';
													let statusText = '';

													bgColor = statusMeta.bgColor;
													statusSymbol = statusMeta.statusSymbol;
													dateText = statusMeta.dateText;
													statusText = statusMeta.statusText;

													// For overdue, show both the date and "Overdue" text
													if (statusClass === 'red') {
														return `
															<td style="border: 0.5px solid #000; padding: ${cellPadding}; text-align: center; background-color: ${bgColor}; vertical-align: middle;">
																<div style="font-size: ${parseInt(fontSize) + 1}px; font-weight: bold;">${statusSymbol}</div>
																<div style="font-size: 5.5px; margin-top: 1px; color: #dc3545; font-weight: bold;">${dateText}</div>
																<div style="font-size: 5.5px; margin-top: 1px; color: #dc3545; font-weight: bold;">${statusText}</div>
															</td>
														`;
													} else {
														return `
															<td style="border: 0.5px solid #000; padding: ${cellPadding}; text-align: center; background-color: ${bgColor}; vertical-align: middle;">
																<div style="font-size: ${parseInt(fontSize) + 1}px; font-weight: bold;">${statusSymbol}</div>
																<div style="font-size: 5.5px; margin-top: 1px; ${statusClass === 'yellow' ? 'color: #856404;' : ''}">${dateText || statusText}</div>
															</td>
														`;
													}
												}
												return `
													<td style="border: 0.5px solid #000; padding: ${cellPadding}; text-align: center; background-color: #f8f9fa; color: #adb5bd;">
														—
													</td>
												`;
											})
											.join('')}
									</tr>
								`;
							})
							.join('')}
					</tbody>
				</table>
			</div>
			
			<!-- Legend -->
			<div style="margin-top: 3px; padding: 2px; border: 0.5px solid #ddd; background-color: #f9f9f9; font-size: 5.5px;">
				<div style="display: flex; gap: 6px; justify-content: center; flex-wrap: wrap;">
					<div style="display: flex; align-items: center; gap: 2px;">
						<div style="width: 8px; height: 8px; background-color: #d4edda; border: 0.5px solid #c3e6cb;"></div>
						<span>✓ Administered</span>
					</div>
					<div style="display: flex; align-items: center; gap: 2px;">
						<div style="width: 8px; height: 8px; background-color: #fff3cd; border: 0.5px solid #ffeeba;"></div>
						<span>⚠ Due</span>
					</div>
					<div style="display: flex; align-items: center; gap: 2px;">
						<div style="width: 8px; height: 8px; background-color: #f8d7da; border: 0.5px solid #f5c6cb;"></div>
						<span>⚠ Overdue (date + text)</span>
					</div>
					<div style="display: flex; align-items: center; gap: 2px;">
						<div style="width: 8px; height: 8px; background-color: #e9ecef; border: 0.5px solid #dee2e6;"></div>
						<span>○ Not Due</span>
					</div>
				</div>
			</div>
			
			<!-- Footer -->
			<div class="print-footer" style="margin-top: 2px; text-align: center; font-size: 5px; color: #666;">
				<hr style="margin: 1px 0;" />
				<p>Official Immunization Record | Deda Hospital | This is a computer-generated document</p>
			</div>
		</div>
	`;

		// Write content to iframe
		printDocument.open();
		printDocument.write(`
		<!DOCTYPE html>
		<html>
		<head>
			<title>Immunization Chart - ${patient?.other_names} ${patient?.surname}</title>
			<meta charset="UTF-8">
			${stylesHTML}
			<style>
				* {
					-webkit-print-color-adjust: exact;
					print-color-adjust: exact;
					margin: 0;
					padding: 0;
					box-sizing: border-box;
				}
				
				body {
					font-family: Arial, Helvetica, sans-serif;
					margin: 0;
					padding: 0;
					background-color: white;
				}
				
				@page {
					size: landscape;
					margin: 0.1in;
				}
				
				/* Force everything on one page */
				html, body {
					height: auto;
					overflow: visible;
				}
				
				/* REMOVE ALL PURPLE/GRADIENT BACKGROUNDS */
				* {
					background-image: none !important;
					box-shadow: none !important;
					text-shadow: none !important;
				}
				
				/* Override any gradient backgrounds */
				.modal-content,
				.modal-header,
				.modal-body,
				.modal-footer,
				.popover,
				.ant-popover,
				.ant-popover-content,
				.ant-popover-inner,
				.ant-popover-inner-content {
					background: none !important;
					background-color: transparent !important;
					background-image: none !important;
					box-shadow: none !important;
				}
				
				/* Ensure all backgrounds are solid white */
				.immunization-print-container,
				.print-header,
				.print-footer,
				.hospital-header {
					background-color: white !important;
					background-image: none !important;
				}
				
				/* Keep table backgrounds clean */
				.immunization-table td,
				.immunization-table th {
					background-color: white !important;
				}
				
				/* Keep status colors but remove any gradients */
				td[style*="background-color: #d4edda"] {
					background-color: #d4edda !important;
					background-image: none !important;
				}
				
				td[style*="background-color: #fff3cd"] {
					background-color: #fff3cd !important;
					background-image: none !important;
				}
				
				td[style*="background-color: #f8d7da"] {
					background-color: #f8d7da !important;
					background-image: none !important;
				}
				
				td[style*="background-color: #e9ecef"] {
					background-color: #e9ecef !important;
					background-image: none !important;
				}
				
				td[style*="background-color: #f8f9fa"] {
					background-color: #f8f9fa !important;
					background-image: none !important;
				}
				
				/* Force single page */
				@media print {
					body {
						margin: 0;
						padding: 0;
						-webkit-print-color-adjust: exact;
						print-color-adjust: exact;
					}
					
					.immunization-print-container {
						width: 100%;
						height: auto;
						page-break-after: avoid;
						page-break-inside: avoid;
						break-inside: avoid;
					}
					
					table {
						page-break-inside: avoid;
						break-inside: avoid;
					}
					
					tr {
						page-break-inside: avoid;
						break-inside: avoid;
					}
					
					thead {
						display: table-header-group;
					}
					
					tbody {
						display: table-row-group;
					}
				}
				
				.immunization-print-container {
					width: 100%;
					height: auto;
				}
				
				.immunization-table {
					width: 100%;
					border-collapse: collapse;
					table-layout: fixed;
				}
				
				.immunization-table th:first-child {
					width: 150px;
				}
				
				.immunization-table th:not(:first-child) {
					width: auto;
				}
				
				.immunization-table th,
				.immunization-table td {
					word-break: break-word;
					overflow-wrap: break-word;
				}
				
				.table-responsive {
					overflow: visible !important;
					width: 100%;
				}
				
				.hospital-header img {
					max-height: 35px;
					width: auto;
				}
			</style>
		</head>
		<body>
			${tableHTML}
			<script>
				window.onload = function() {
					setTimeout(function() {
						window.print();
						setTimeout(function() {
							window.parent.document.body.removeChild(window.frameElement);
						}, 1000);
					}, 300);
				};
			</script>
		</body>
		</html>
	`);
		printDocument.close();
	};

	return (
		<div id='immunization'>
			<Modal.Header closeButton>
				{/* <Modal.Title>
					<i className="os-icon os-icon-graph-bar mr-2" />
					Immunization Chart - {patient?.other_names} {patient?.surname}
				</Modal.Title> */}
			</Modal.Header>
			<Modal.Body style={{ padding: '20px', maxHeight: '80vh', overflow: 'hidden' }}>
				{loading ? (
					<div className="text-center p-5">
						<div className="spinner-border text-primary" role="status">
							<span className="sr-only">Loading...</span>
						</div>
					</div>
				) : records.length === 0 ? (
					<div className="text-center p-5 text-muted">
						<i className="os-icon os-icon-chart-line fa-3x mb-3" />
						<p>No immunization records found for this patient</p>
					</div>
				) : (
					<>
						{/* Visible table for the modal */}
						<div className="immunization-chart-grid">
							<table
								className="table table-bordered table-md table-v2 table-striped immunization-table"
								style={{ minWidth: `${Math.max(720, 220 + visibleDates.length * 96)}px` }}
							>
								<thead className="bg-light">
									<tr>
										<th
											className="sticky-col"
											style={{ minWidth: '220px', backgroundColor: '#fff', position: 'sticky', left: 0 }}
										>
											Vaccine
										</th>
										{visibleDates.map((title, i) => (
											<th key={i} className="text-center" style={{ minWidth: '96px' }}>
												{title}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{records.map((items, i) => {
										const item = items[0];
										return (
											<tr key={i}>
												<td className="sticky-col" style={{ backgroundColor: '#fff', position: 'sticky', left: 0 }}>
													<div>
														<strong>{item.name_of_vaccine}</strong>
														<small className="d-block text-muted">{item.description}</small>
													</div>
												</td>
												{visibleDates.map((date, idx) => {
													const data = items.find(i => i.period === date);
													if (!data) {
														return (
															<td key={idx} className="text-center text-muted p-0">
																<div className="vaccine-cell vaccine-cell-empty">—</div>
															</td>
														);
													}

													const statusMeta = getStatusMeta(data);

													return (
														<td key={idx} className="text-center p-0">
															<Popover
																content={renderPopoverContent(data)}
																overlayClassName="view-vaccine"
																trigger="hover"
																placement="top"
																zIndex={2000}
															>
																<div
																	className={`vaccine-cell ${statusMeta.statusClass}`}
																>
																	<div className="vaccine-cell-inner">
																		<i className={`${statusMeta.statusIcon} vaccine-cell-icon`} aria-hidden="true" />
																		<div className="vaccine-cell-date">{statusMeta.dateText || 'No date'}</div>
																		<div className="vaccine-cell-status">{statusMeta.statusText}</div>
																	</div>
																</div>
															</Popover>
														</td>
													);
												})}
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{records.length > 0 && (
					<Button variant="primary" onClick={exportToPDF}>
						<i className="fa fa-print mr-2"></i>
						Export as PDF
					</Button>
				)}
				
			</Modal.Footer>
		</div>
	);
};

// Updated CSS for full-width modal
const styles = `
/* Full width modal */
.modal-fullwidth {
    max-width: 95% !important;
    width: 95% !important;
    margin: 1.75rem auto;
}

@media (min-width: 992px) {
    .modal-fullwidth {
        max-width: 98% !important;
        width: 98% !important;
    }
}

@media (min-width: 1200px) {
    .modal-fullwidth {
        max-width: 95% !important;
        width: 95% !important;
    }
}

.immunization-table {
    position: relative;
    width: max-content;
    margin-bottom: 0;
    table-layout: fixed;
    background: #fff;
}

.immunization-chart-grid {
    max-height: calc(80vh - 110px);
    overflow: auto;
    border: 1px solid #dfe3e8;
    border-radius: 14px;
    background: #fff;
}

.immunization-table .sticky-col {
    position: sticky;
    left: 0;
    background-color: #fff;
    z-index: 3;
    box-shadow: 8px 0 16px -14px rgba(15, 23, 42, 0.45);
}

.immunization-table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8fafc;
    white-space: nowrap;
    vertical-align: middle;
}

.immunization-table thead .sticky-col {
    z-index: 4;
}

.immunization-table td,
.immunization-table th {
    border-color: #dfe3e8 !important;
}

.immunization-table td {
    vertical-align: middle;
}

.vaccine-cell {
    min-height: 76px;
    padding: 8px 6px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.vaccine-cell:hover {
    transform: translateY(-1px);
    box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06);
}

.vaccine-cell.green {
    background-color: #d4edda;
}

.vaccine-cell.yellow {
    background-color: #fff3cd;
}

.vaccine-cell.red {
    background-color: #f8d7da;
}

.vaccine-cell.grey {
    background-color: #e9ecef;
}

.vaccine-cell-empty {
    background: #f8fafc;
    color: #94a3b8;
    font-weight: 600;
}

.vaccine-cell-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    line-height: 1.2;
}

.vaccine-cell-icon {
    font-size: 15px;
}

.vaccine-cell.green .vaccine-cell-icon {
    color: #1f9254;
}

.vaccine-cell.yellow .vaccine-cell-icon {
    color: #b58105;
}

.vaccine-cell.red .vaccine-cell-icon {
    color: #c0392b;
}

.vaccine-cell.grey .vaccine-cell-icon {
    color: #64748b;
}

.vaccine-cell-date {
    font-size: 11px;
    font-weight: 700;
    color: #1f2937;
    text-align: center;
}

.vaccine-cell-status {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #475569;
    text-align: center;
}

/* Tooltip/Popover styles */
.view-vaccine.ant-popover {
    z-index: 2000;
}

.view-vaccine .ant-popover-content {
    min-width: 260px;
}

.view-vaccine .ant-popover-inner-content {
    padding: 0 !important;
}

.vaccine-popover-card {
    padding: 14px;
    background: #fff;
}

.vaccine-popover-badge {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 700;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.vaccine-popover-badge.green {
    background: #dff3e7;
    color: #1f9254;
}

.vaccine-popover-badge.yellow {
    background: #fff4d0;
    color: #9a6700;
}

.vaccine-popover-badge.red {
    background: #fde2e1;
    color: #c0392b;
}

.vaccine-popover-badge.grey {
    background: #edf2f7;
    color: #475569;
}

.vaccine-popover-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
    border-bottom: 1px solid #eef2f7;
    font-size: 12px;
}

.vaccine-popover-row span {
    color: #64748b;
}

.vaccine-popover-row strong {
    color: #0f172a;
    text-align: right;
}

.vaccine-popover-note {
    margin-top: 10px;
    font-size: 12px;
    color: #475569;
    line-height: 1.5;
}

/* Modal body scroll */
.immunization-chart-grid::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.immunization-chart-grid::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.immunization-chart-grid::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.immunization-chart-grid::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* Empty state */
.empty-state {
    text-align: center;
    padding: 40px;
    color: #6c757d;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
}
`;

// Inject styles if not already present
if (!document.querySelector('#immunization-modal-styles')) {
	const styleSheet = document.createElement('style');
	styleSheet.id = 'immunization-modal-styles';
	styleSheet.textContent = styles;
	document.head.appendChild(styleSheet);
}

export default ImmunizationChart;
